import { CatalogScope } from '../../models/catalog-scope.enum';
import { TemperatureDerationCatalog } from '../../models/temperature-deration-catalog.model';

export type TemperatureDerationLike = Partial<TemperatureDerationCatalog>;

export function findTemperatureDerationIndex(
  items: TemperatureDerationLike[],
  selected: TemperatureDerationLike | null
): number {
  if (!selected) {
    return -1;
  }

  let index = items.findIndex((x) => x === selected);
  if (index !== -1) {
    return index;
  }

  return items.findIndex(
    (x) => x.id && selected.id && String(x.id).toLowerCase() === String(selected.id).toLowerCase()
  );
}

export function getTemperatureDerationProfileKey(
  item: TemperatureDerationLike,
  index: number,
  emptyGuid: string
): string {
  const normalizedId = item?.id ? String(item.id).toLowerCase() : '';
  return normalizedId && normalizedId !== emptyGuid.toLowerCase()
    ? normalizedId
    : `new:${index}`;
}

export function buildTemperatureDerationFingerprint(item: TemperatureDerationLike): string {
  const profile = (item.temperatureDerationProfile ?? []).map((point) => ({
    temperature: Number(point.temperature),
    derationFactor: Number(point.derationFactor)
  }));

  return JSON.stringify({
    name: item.name?.trim() ?? '',
    scope: item.scope ?? CatalogScope.User,
    profile
  });
}

export function buildTemperatureDerationFingerprintMap(
  items: TemperatureDerationLike[],
  emptyGuid: string
): Record<string, string> {
  return items.reduce<Record<string, string>>((acc, item, index) => {
    const key = getTemperatureDerationProfileKey(item, index, emptyGuid);
    acc[key] = buildTemperatureDerationFingerprint(item);
    return acc;
  }, {});
}

export function upsertSavedTemperatureDerationFingerprint(
  existing: Record<string, string>,
  item: TemperatureDerationLike,
  index: number,
  emptyGuid: string,
  previousKey?: string
): Record<string, string> {
  const next = { ...existing };

  if (previousKey && previousKey in next) {
    delete next[previousKey];
  }

  next[getTemperatureDerationProfileKey(item, index, emptyGuid)] = buildTemperatureDerationFingerprint(item);
  return next;
}

export function hasDuplicateTemperatureDerationName(items: TemperatureDerationLike[], index: number): boolean {
  const item = items[index];
  const name = item?.name?.trim();
  if (!name) {
    return false;
  }

  return items.some((x, i) =>
    i !== index &&
    x.name &&
    x.name.trim().toLowerCase() === name.toLowerCase() &&
    (!x.id || !item.id || String(x.id).toLowerCase() !== String(item.id).toLowerCase())
  );
}
